/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { Switch, Route, BrowserRouter, Redirect } from 'react-router-dom';
import { MainLayout } from '@eriksdigital/atomic-ui/components';
import { breakpointObj } from '@eriksdigital/atomic-ui/styles';
import {
  selectLanguage,
  selectBasenamePath,
} from './features/erpSystem/slice/selectors';
import { salesOrganizationActions } from 'app/features/salesOrganisation/slice/';
import { userActions } from './features/user/slice';
import { Header } from 'app/components/organisms';
import { ConfiguratorPage } from 'app/pages/ConfiguratorPage/Loadable';
import { SelectorPage } from 'app/pages/SelectorPage/Loadable';
import { selectModalShow } from './features/modal/slice/selectors';
import { Modal, MobileSupport } from 'app/components/molecules';
import { modalActions } from 'app/features/modal/slice';

const fetchTranslations = async ({ language = 'en' }) => {
  try {
    if (process.env.REACT_APP_USE_MOCK_TRANSLATIONS === 'YES') {
      const module = await import(`./../mocks/mockLocales/${language}`);
      return await module.default;
    } else {
      const module = await import(`../../locales/${language}`);
      return await module.default;
    }
  } catch (e) {
    throw e;
  }
};

const App = () => {
  const dispatch = useDispatch();
  const language = useSelector(selectLanguage);
  const { getUser } = userActions;
  const basenamePath = useSelector(selectBasenamePath);
  const { getSalesOrganization } = salesOrganizationActions;
  const [messages, setMessages] = useState(fetchTranslations({ language }));
  const showModal = useSelector(selectModalShow);
  const { openModal } = modalActions;

  useEffect(() => {
    dispatch(getSalesOrganization());
  }, [dispatch, getSalesOrganization]);

  useEffect(() => {
    dispatch(getUser());
  }, [dispatch, getUser]);

  useEffect(() => {
    fetchTranslations({ language }).then(translation =>
      setMessages(translation),
    );
  }, [language]);

  const routes = {
    SelectorPage: { component: SelectorPage, path: `${basenamePath}/selector` },
    ConfiguratorPage: {
      component: ConfiguratorPage,
      path: `${basenamePath}/:erpPartNumber`,
    },
  };

  useEffect(() => {
    if (window.innerWidth < breakpointObj.tabletPortrait) {
      // TODO: Remove modal after epic https://eriksdigital.atlassian.net/browse/GB-1193 is done
      dispatch(
        openModal({
          show: true,
          content: {
            type: 'TextContent',
            textElement: <MobileSupport />,
          },
        }),
      );
    }
  }, [dispatch, openModal, language]);

  return (
    // @ts-ignore
    <IntlProvider locale={language} messages={messages}>
      <BrowserRouter>
        <Helmet
          titleTemplate="%s"
          defaultTitle="Gasket Configurator"
          htmlAttributes={{ lang: language }}
        >
          <meta
            name="description"
            content="A Gasket Configurator application"
          />
        </Helmet>
        <Header />
        <MainLayout>
          <Switch>
            {Object.entries(routes).map(route => {
              const [key, value] = route;
              return (
                <Route
                  key={key}
                  path={value.path}
                  component={value.component}
                />
              );
            })}
            <Redirect from="*" to={`${basenamePath}/selector`} />
          </Switch>
          {showModal && <Modal />}
        </MainLayout>
      </BrowserRouter>
    </IntlProvider>
  );
};

export default App;
